import { memo, useCallback, useRef } from 'react';
import type { GridRowIdGetter } from "@mui/x-data-grid";
import { TableGridMode } from 'consts/table';
import { date, FilterVariant } from 'consts/filters';
import { multi, single, text, TransactionTableClassNames } from 'consts/transactions';
import { when } from 'util/support';
import { useTypedSelector } from 'hooks';
import { selectTransactionCurrencies } from 'features/general/selectors';
import {
    selectIsTransactionsLoading,
    selectIsTransactionsUninitialized,
    selectTransactionSlice
} from 'features/transactions/selectors';
import useActions from 'features/transactions/useActions';
import type { Transaction } from 'features/transactions/types';
import Table, { FiltersPublicApi } from "ui/widgets/Table";
import { FilterRenderTypes, useFiltersEvents } from 'ui/widgets/Filters';
import * as components from './components';
import {
    useRequestQueryOverrideDecorator,
    useFilterPropsOverride,
    useFiltersVisibilityRules as useVisibilityRules
} from './hooks';
import TransactionDetails from '../TransactionDetails';

const TransactionTable = () => {
    const filtersRef = useRef<FiltersPublicApi<string> | null>(null);

    const Currencies = useTypedSelector(selectTransactionCurrencies);

    const {
        getTransactions,
        downloadCsv,
    } = useActions();

    const getRowId: GridRowIdGetter<Transaction> = useCallback(({ transactionId }: Transaction) =>
        transactionId, []);

    useFiltersEvents({
        [FilterRenderTypes.PopoverDropdown]: {
            onApply: () => filtersRef.current?.onApply()
        }
    });

    return (
        <Table
            filtersRef={filtersRef}
            mode={TableGridMode.Transaction}
            selectIsTableUninitialized={selectIsTransactionsUninitialized}
            selectIsTableLoading={selectIsTransactionsLoading}
            selectTableDataSlice={selectTransactionSlice}
            fetchTableData={getTransactions}
            downloadCsv={downloadCsv}
            getRowClassName={({ row }) => when(
                row.children.length,
                TransactionTableClassNames.HasChildren
            )}
            filterTypes={new Map<FilterVariant, string[]>([
                [FilterVariant.Text, text],
                [FilterVariant.DateRange, date],
                [FilterVariant.MultiOption, multi],
                [FilterVariant.SingleOption, single]
            ])}
            components={components}
            DetailPanelContent={TransactionDetails}
            getRowId={getRowId}
            requestQueryOverrideDecorator={useRequestQueryOverrideDecorator}
            LayoutProps={{
                sx: {
                    m: '24px 0px',
                    [`& .${TransactionTableClassNames.HasChildren}`]: {
                        bgcolor: theme => `${theme.palette.error.light} !important`
                    }
                }
            }}
            FiltersProps={{
                sx: {
                    m: 0
                },
                useVisibilityRules,
                useFilterPropsOverride,
                filtersModelOverrides: {
                    Currencies
                }
            }}
        />
    );
};

export default memo(TransactionTable);
